// ────────────────────────────────────────────────────────────────────────────
// DocRefContext
// ────────────────────────────────────────────────────────────────────────────
// Permite abrir cualquier documento referenciado (OC, Factura, Nota de Crédito)
// en un modal, sin salir de la pantalla actual. Renderiza UN solo modal global.
//
// Uso:
//   import { DocRefLink } from '../components/DocRefLink';
//   <DocRefLink tipo="oc" numero={h.numero_oc}>{h.numero_oc}</DocRefLink>
//   <DocRefLink tipo="dte" folio={f.folio} rut={f.rut_emisor} label={`FE-${f.folio}`} />
//
// O imperativamente:  const { openDoc } = useDocRef(); openDoc({ tipo:'oc', numero });
// ────────────────────────────────────────────────────────────────────────────
import { createContext, useCallback, useContext, useState } from 'react';
import api from '../lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Loader2, Package, FileText, AlertCircle } from 'lucide-react';
import { OCViewer } from '../components/OCViewer';
import { FacturaViewer } from '../components/FacturaViewer';

const DocRefContext = createContext(null);

const EMPTY = { open: false, tipo: null, titulo: '', loading: false, error: '', data: null };

export function DocRefProvider({ children }) {
  const [state, setState] = useState(EMPTY);

  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);

  const openDoc = useCallback(async ({ tipo, numero, folio, rut }) => {
    if (tipo === 'oc') {
      if (!numero) return;
      setState({ open: true, tipo, titulo: `Orden de Compra ${numero}`, loading: true, error: '', data: null });
      try {
        const res = await api.get(`/compras/orden-compra/${encodeURIComponent(numero)}/detalle-completo`);
        setState({ open: true, tipo, titulo: `Orden de Compra ${numero}`, loading: false, error: '', data: res.data });
      } catch (e) {
        const msg = e?.response?.status === 404
          ? `No se encontró la OC ${numero} en el sistema.`
          : (e?.response?.data?.detail || 'No se pudo cargar la orden de compra.');
        setState({ open: true, tipo, titulo: `Orden de Compra ${numero}`, loading: false, error: msg, data: null });
      }
    } else if (tipo === 'dte') {
      if (!folio) return;
      const titulo = `Documento folio ${folio}`;
      setState({ open: true, tipo, titulo, loading: true, error: '', data: null });
      try {
        const params = rut ? { rut_emisor: rut } : {};
        const res = await api.get(`/contabilidad/acepta-rechazo-dte/detalle/${encodeURIComponent(folio)}`, { params });
        const dte = res.data?.dte || null;
        const nombre = dte?.tipo_dte_nombre || 'Documento';
        setState({
          open: true, tipo, titulo: `${nombre} N° ${folio}`, loading: false, error: '',
          data: { dte, operaciones: res.data?.operaciones || [] },
        });
      } catch (e) {
        const msg = e?.response?.status === 404
          ? `No se encontró el documento folio ${folio} en el sistema.`
          : (e?.response?.data?.detail || 'No se pudo cargar el documento.');
        setState({ open: true, tipo, titulo, loading: false, error: msg, data: null });
      }
    }
  }, []);

  const Icon = state.tipo === 'oc' ? Package : FileText;

  return (
    <DocRefContext.Provider value={{ openDoc }}>
      {children}
      <Dialog open={state.open} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-[720px] max-h-[88vh] overflow-y-auto p-4" data-testid="docref-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm" data-testid="docref-modal-title">
              <Icon className="w-4 h-4 text-[#003366]" /> {state.titulo}
            </DialogTitle>
          </DialogHeader>

          {state.loading && (
            <div className="flex justify-center py-10" data-testid="docref-loading">
              <Loader2 className="w-6 h-6 animate-spin text-[#003366]" />
            </div>
          )}

          {!state.loading && state.error && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-md px-3 py-3 text-sm text-amber-800"
              data-testid="docref-error">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{state.error}</span>
            </div>
          )}

          {!state.loading && !state.error && state.tipo === 'oc' && state.data && (
            <OCViewer
              oc={state.data.oc}
              detalle={state.data.detalle || []}
              proveedor={state.data.proveedor || {}}
              operaciones={state.data.operaciones || []}
              formaPago={state.data.forma_pago || ''}
              esTarjeta={state.data.es_tarjeta || false}
            />
          )}

          {!state.loading && !state.error && state.tipo === 'dte' && state.data?.dte && (
            <FacturaViewer dte={state.data.dte} operaciones={state.data.operaciones || []} />
          )}

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={close} data-testid="docref-close">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DocRefContext.Provider>
  );
}

export function useDocRef() {
  const ctx = useContext(DocRefContext);
  if (!ctx) throw new Error('useDocRef debe usarse dentro de <DocRefProvider>');
  return ctx;
}
